import { MarketInputTextCustomEvent } from '@market/web-components/loader';
import {
  AccessTokensData,
  AccessTokenAPIError,
} from '@squareup/dex-store-access-shared-main-use-access-tokens-hook';
import { ApplicationType } from '@squareup/dex-types-shared-application-type';
import { useIsMarketComponentLoaded } from '@squareup/dex-ui-hooks';
import {
  ApiExplorerAccessTokenContext,
  useApiExplorerContext,
} from '@squareup/dex-ui-shared-api-explorer-providers';
import { Box, Paragraph20 } from '@squareup/dex-ui-shared-base';
import {
  MarketAccessory,
  MarketButton,
  MarketDropdown,
  MarketInputText,
} from '@squareup/dex-ui-shared-market';
import {
  getNextPublicBaseURL,
  getNextPublicSquareURL,
} from '@squareup/dex-utils-environment';
import { usePathname } from '@squareup/dex-utils-shared-routing-hooks';
import clsx from 'clsx';
import React, {
  FC,
  MouseEventHandler,
  useCallback,
  useRef,
  useState,
} from 'react';

import { AccessTokenPopover } from './AccessTokenPopover';
import styles from './access-token-field.module.css';

const strings = {
  accessToken: 'Access token',
  cancel: 'Cancel',
  change: 'Change',
  select: 'Select',
  selectToken: 'Select token',
  signIn: 'Sign in',
  signInPrompt: 'Sign in to view access tokens',
};

type Props = {
  accessTokens: AccessTokensData[keyof AccessTokensData] | undefined;
  environment: ApplicationType;
  errors: AccessTokenAPIError[];
  isAuthenticated: boolean;
  isLoading: boolean;
  supportedEnvironments: ApplicationType[];
  value: string;
};

const AccessTokenField: FC<Props> = ({
  accessTokens,
  environment,
  errors,
  isAuthenticated,
  isLoading,
  supportedEnvironments,
  value,
}) => {
  const { pathName } = usePathname();
  const { dispatchAccessTokenAction } = useApiExplorerContext();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);

  const ref = useRef<globalThis.HTMLMarketDropdownElement>(null);
  const trailAccRef = useRef<globalThis.HTMLMarketButtonElement>(null);
  const [isMarketComponentLoaded, onMarketRef] = useIsMarketComponentLoaded(
    ref,
    'market-dropdown'
  );

  const loginUrl = `${getNextPublicSquareURL()}/login?app=developer&return_to=${getNextPublicBaseURL()}${pathName}`;

  const buttonText = () => {
    switch (true) {
      case isDropdownOpen:
        return strings['cancel'];
      case Boolean(value):
        return strings['change'];
      case isAuthenticated:
        return strings['select'];
      default:
        return strings['signIn'];
    }
  };

  // Function to handle the selection of a token. Sets the selected token, environment, and session tokens
  const handleTokenSelect = useCallback(
    (payload: ApiExplorerAccessTokenContext) => {
      dispatchAccessTokenAction({
        type: 'SET',
        payload,
      });
      setIsDropdownOpen(false);
    },
    [dispatchAccessTokenAction]
  );
  const onDropdownToggle = useCallback(
    (state: boolean) => () => setIsDropdownOpen(state),
    []
  );
  const onFocusToggle = useCallback(
    (state: boolean) => () => setHasFocus(state),
    []
  );
  const onInputButtonClick = useCallback<
    MouseEventHandler<globalThis.HTMLMarketButtonElement>
  >(
    (e) => {
      isDropdownOpen && setHasFocus(false);
      if (
        !isAuthenticated &&
        !value &&
        trailAccRef.current?.contains(e.target as HTMLElement)
      ) {
        // If the user is not authenticated with no input value, the button should navigate to the login page
        // and the dropdown should not open
        ref.current?.closeDropdown();
      }
    },
    [isDropdownOpen, isAuthenticated, value]
  );
  const onInputValueChange = useCallback(
    ({
      detail: { value: inputValue },
    }: MarketInputTextCustomEvent<{ value: string }>) => {
      dispatchAccessTokenAction({ type: 'SET_TOKEN', payload: inputValue });
    },
    [dispatchAccessTokenAction]
  );

  return (
    <Box
      className={clsx(
        styles['token-field'],
        !isMarketComponentLoaded && styles['hidden']
      )}
    >
      <MarketDropdown
        className={styles['token-dropdown'] as string}
        expanded={isDropdownOpen}
        interaction="persistent"
        onMarketDropdownClosed={onDropdownToggle(false)}
        onMarketDropdownOpened={onDropdownToggle(true)}
        popoverPlacement="bottom-start"
        ref={onMarketRef}
      >
        <MarketInputText
          autocomplete={false}
          className={clsx(
            styles['token-input'],
            !hasFocus && styles['dim-input']
          )}
          focused={hasFocus}
          onBlur={onFocusToggle(false)}
          onFocus={onFocusToggle(true)}
          onMarketInputValueChange={onInputValueChange}
          placeholder={
            isAuthenticated ? strings['selectToken'] : strings['signInPrompt']
          }
          size="small"
          slot="trigger"
          testId="access-token-input"
          trackingId="api-explorer-access-token-field"
          value={value}
        >
          <MarketAccessory
            className={styles['input-accessory'] as string}
            slot="leading-accessory"
          >
            {value && !hasFocus && (
              <Paragraph20 weight="semi-bold">
                {strings['accessToken']}&nbsp;
                <Paragraph20
                  as="span"
                  className={styles[`env-${environment.toLowerCase()}`]}
                  weight="semi-bold"
                >
                  {environment}
                </Paragraph20>
              </Paragraph20>
            )}
          </MarketAccessory>
          <MarketButton
            href={
              !isAuthenticated && !isDropdownOpen && !value
                ? loginUrl
                : undefined
            }
            onClick={onInputButtonClick}
            ref={trailAccRef}
            rank="tertiary"
            size="small"
            slot="trailing-accessory"
            testId="access-token-input-button"
            trackingId="access-token-input-button"
            className={styles['token-action-button'] as string}
          >
            {buttonText()}
          </MarketButton>
        </MarketInputText>

        <AccessTokenPopover
          accessTokens={accessTokens}
          environment={environment}
          errors={errors}
          isAuthenticated={isAuthenticated}
          isLoading={isLoading}
          onTokenSelect={handleTokenSelect}
          selectedToken={value}
          supportedEnvironments={supportedEnvironments}
        />
      </MarketDropdown>
    </Box>
  );
};

export { AccessTokenField };
